/**
 * Sitemap generation utilities for AfterHours
 * Generates dynamic sitemaps for published Nostr git projects
 */

import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';

export interface SitemapUrl {
  loc: string;
  lastmod?: string;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority?: number;
}

/**
 * Generate sitemap XML from URL objects
 */
export function generateSitemapXML(urls: SitemapUrl[]): string {
  const urlEntries = urls.map((url) => {
    let entry = `  <url>\n    <loc>${escapeXml(url.loc)}</loc>`;
    
    if (url.lastmod) {
      entry += `\n    <lastmod>${url.lastmod}</lastmod>`;
    }
    
    if (url.changefreq) {
      entry += `\n    <changefreq>${url.changefreq}</changefreq>`;
    }
    
    if (url.priority !== undefined) {
      entry += `\n    <priority>${url.priority.toFixed(1)}</priority>`;
    }
    
    entry += '\n  </url>';
    return entry;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urlEntries}
</urlset>`;
}

/**
 * Escape special XML characters
 */
function escapeXml(unsafe: string): string {
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case "'": return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
}

/**
 * Convert a Nostr timestamp (seconds) to ISO 8601 date string
 */
function nostrTimestampToISO(timestamp: number): string {
  return new Date(timestamp * 1000).toISOString();
}

/**
 * Generate sitemap URLs for static pages
 */
export function getStaticPageUrls(): SitemapUrl[] {
  const baseUrl = 'https://afterhours.diy';
  
  return [
    {
      loc: baseUrl,
      changefreq: 'daily',
      priority: 1.0,
    },
    {
      loc: `${baseUrl}/explore`,
      changefreq: 'hourly',
      priority: 0.9,
    },
  ];
}

/**
 * Generate sitemap URLs for user profile pages
 */
export function getProfileUrls(pubkeys: string[]): SitemapUrl[] {
  const baseUrl = 'https://afterhours.diy';
  
  return pubkeys.map((pubkey) => {
    const npub = nip19.npubEncode(pubkey);
    return {
      loc: `${baseUrl}/${npub}`,
      changefreq: 'weekly',
      priority: 0.7,
    };
  });
}

/**
 * Generate sitemap URLs for project pages
 */
export function getProjectUrls(projects: NostrEvent[]): SitemapUrl[] {
  const baseUrl = 'https://afterhours.diy';
  
  return projects.map((project) => {
    const dTag = project.tags.find((tag) => tag[0] === 'd')?.[1];
    if (!dTag) return null;
    
    const npub = nip19.npubEncode(project.pubkey);
    
    return {
      loc: `${baseUrl}/${npub}/${dTag}`,
      lastmod: nostrTimestampToISO(project.created_at),
      changefreq: 'weekly',
      priority: 0.8,
    };
  }).filter((url): url is SitemapUrl => url !== null);
}

/**
 * Generate complete sitemap from Nostr data
 */
export function generateCompleteSitemap(
  projects: NostrEvent[],
  profilePubkeys: string[]
): string {
  const staticUrls = getStaticPageUrls();
  const profileUrls = getProfileUrls(profilePubkeys);
  const projectUrls = getProjectUrls(projects);
  
  const allUrls = [...staticUrls, ...profileUrls, ...projectUrls];
  
  return generateSitemapXML(allUrls);
}
